// Reusable homepage discovery primitives.
//
// Used by Phase B (homepage polish) and reusable for Tours, Cruises, Packages,
// Destinations, Offers, Recommendations — anywhere we render a horizontal row of cards.
//
// All primitives are MOBILE-FIRST:
//   • Mobile  → native horizontal scroll with snap, hidden scrollbar, edge fade.
//   • Desktop → adds left/right arrow controls that scroll one card-width at a time.
//
// Public API
// ----------
//   <Section title sub action>              — typography wrapper with consistent spacing
//   <Carousel children itemMinWidth>        — horizontal scroller with arrows on desktop
//   <SkeletonCard h w />                    — shimmer placeholder
//   <SkeletonRow count h />                  — row of N skeleton cards
//
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ── Section wrapper ────────────────────────────────────────────────────
export const Section = ({ title, sub, action, eyebrow, children, className = '' }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    <div className="flex items-end justify-between gap-3 mb-4 sm:mb-6">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-red-600 mb-1">{eyebrow}</p>
        )}
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white truncate">{title}</h2>
        {sub && <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-gray-400">{sub}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

// ── Carousel ──────────────────────────────────────────────────────────
export const Carousel = ({ children, itemMinWidth = 260, ariaLabel = 'carousel' }) => {
  const ref = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update, children]);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    const step = Math.max(itemMinWidth, el.clientWidth * 0.8) * dir;
    el.scrollBy({ left: step, behavior: 'smooth' });
  };

  return (
    <div className="relative group" role="region" aria-label={ariaLabel}>
      {/* Mobile fade-out edges so users *see* there's more content to swipe */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white dark:from-gray-900 to-transparent z-10 sm:hidden" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 sm:hidden" />

      <div
        ref={ref}
        className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar scroll-snap-x pb-2 pr-1"
        data-testid="carousel-track"
      >
        {React.Children.map(children, (child, i) => (
          <div
            key={i}
            className="shrink-0"
            style={{ minWidth: `${itemMinWidth}px`, maxWidth: `${itemMinWidth * 1.4}px`, width: '78%' }}
            data-testid={`carousel-item-${i}`}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Desktop arrow controls */}
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        disabled={!canPrev}
        aria-label="Previous"
        data-testid="carousel-prev"
        className={`hidden sm:flex items-center justify-center absolute top-1/2 -translate-y-1/2 -left-3 h-10 w-10 rounded-full bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 transition-opacity z-20 ${canPrev ? 'opacity-100 hover:bg-gray-50' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        disabled={!canNext}
        aria-label="Next"
        data-testid="carousel-next"
        className={`hidden sm:flex items-center justify-center absolute top-1/2 -translate-y-1/2 -right-3 h-10 w-10 rounded-full bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 transition-opacity z-20 ${canNext ? 'opacity-100 hover:bg-gray-50' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

// ── Skeleton loaders ──────────────────────────────────────────────────
export const SkeletonCard = ({ h = 220, w = '100%', rounded = 'rounded-xl' }) => (
  <div
    className={`relative overflow-hidden bg-gray-100 dark:bg-gray-800 ${rounded}`}
    style={{ height: h, width: w }}
    data-testid="skeleton-card"
  >
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_infinite] bg-gradient-to-r from-transparent via-white/50 dark:via-white/10 to-transparent" />
  </div>
);

export const SkeletonRow = ({ count = 4, h = 220 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
    {Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} h={h} />)}
  </div>
);
